import { useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { AuthError } from '@supabase/supabase-js';

export const useAuthInitialization = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const initialize = async () => {
      try {
        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          toast({
            title: "Session Error",
            description: "There was an error with your session. Please try logging in again.",
            variant: "destructive",
          });
          // Clear any invalid session data
          await supabase.auth.signOut();
        }

        // Set up auth state change listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('Auth state changed:', event);
            
            if (event === 'SIGNED_OUT') {
              // Clear any stored return paths
              localStorage.removeItem('returnTo');
              toast({
                title: "Signed Out",
                description: "You have been successfully signed out.",
              });
            }

            if (event === 'SIGNED_IN') {
              try {
                // Check if profile exists
                const { data: profile, error: profileError } = await supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', session?.user?.id)
                  .maybeSingle();

                if (profileError) {
                  console.error('Profile creation error:', profileError);
                  throw profileError;
                }

                if (!profile) {
                  // If no profile exists, try to create one
                  const { error: insertError } = await supabase
                    .from('profiles')
                    .insert([
                      {
                        id: session?.user?.id,
                        full_name: session?.user?.user_metadata?.full_name || '',
                        role: 'guest'
                      }
                    ]);

                  if (insertError) {
                    console.error('Error creating profile:', insertError);
                    throw insertError;
                  }
                }

                toast({
                  title: "Welcome Back!",
                  description: "You have successfully signed in.",
                });
              } catch (error) {
                console.error('Error handling sign in:', error);
                toast({
                  title: "Profile Error",
                  description: "There was an error setting up your profile. Please try again.",
                  variant: "destructive",
                });
                await supabase.auth.signOut();
              }
            }

            // Handle rate limit errors
            if (event === 'USER_UPDATED' && !session) {
              toast({
                title: "Rate Limit Exceeded",
                description: "Too many login attempts. Please try again later.",
                variant: "destructive",
              });
            }
          }
        );

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Auth initialization error:', error);
        toast({
          title: "Error",
          description: "There was an error initializing the application. Please refresh the page.",
          variant: "destructive",
        });
      } finally {
        setIsInitialized(true);
      }
    };

    initialize();
  }, [toast]);

  return { isInitialized };
};